import type { FC } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
// import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import CollectionsIcon from '@mui/icons-material/Collections';
import { useState, useRef } from 'react';

const solutions = [
    {
        icon: <CurrencyExchangeIcon sx={{ fontSize: 40 }} />,
        title: 'Exchanges',
        description: 'Seamless integration with major exchanges',
        gradient: 'linear-gradient(135deg, #FF6B00 0%, #FF9D00 100%)',
        shadowColor: '#FF6B00',
        accentColor: '#FFB800'
    },
    {
        icon: <PublicIcon sx={{ fontSize: 40 }} />,
        title: 'Cross Border Payments',
        description: 'Global transactions made simple',
        gradient: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
        shadowColor: '#6366F1',
        accentColor: '#818CF8'
    },
    {
        icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
        title: 'Wallets',
        description: 'Secure and easy-to-use wallets',
        gradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
        shadowColor: '#10B981',
        accentColor: '#34D399'
    },
    {
        icon: <CollectionsIcon sx={{ fontSize: 40 }} />,
        title: 'NFTs',
        description: 'Trade digital collectibles',
        gradient: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
        shadowColor: '#EC4899',
        accentColor: '#F472B6'
    },
];




const MotionCard = motion(Card);

export const HomeHowItHelpsHowItWorks: FC = () => {
    
    const containerRef = useRef(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                pt: { xs: 12, md: 16 },
                pb: { xs: 12, md: 16 },
            }}
        >
            {/* Animated background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.05,
                    background: 'radial-gradient(circle at 50% 50%, #6366F1 0%, transparent 70%)',
                    filter: 'blur(100px)',
                    animation: 'pulse 8s ease-in-out infinite',
                    '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.5)' }
                    }
                }}
            />

            {/* Animated grid pattern */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.05,
                    backgroundImage: `linear-gradient(#6366F1 1px, transparent 1px),
                                    linear-gradient(90deg, #6366F1 1px, transparent 1px)`,
                    backgroundSize: '50px 50px',
                    animation: 'gridMove 20s linear infinite',
                    '@keyframes gridMove': {
                        '0%': { transform: 'translate(0, 0)' },
                        '100%': { transform: 'translate(50px, 50px)' }
                    }
                }}
            />

            <Container maxWidth="lg">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            textAlign: 'center',
                            mb: { xs: 8, md: 10 },
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 50%, #C4B5FD 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 2px 10px rgba(99, 102, 241, 0.2)',
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '100px',
                                height: '4px',
                                background: 'linear-gradient(90deg, transparent, #6366F1, transparent)',
                                borderRadius: '2px',
                            }
                        }}
                    >
                        Supercharge Your Business
                    </Typography>
                </motion.div>

                <Stack
                    direction="row"
                    spacing={3}
                    sx={{
                        flexWrap: 'wrap',
                        gap: 3,
                        justifyContent: 'center',
                    }}
                >
                    {solutions.map((solution, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                            <MotionCard
                                onHoverStart={() => setHoveredIndex(index)}
                                onHoverEnd={() => setHoveredIndex(null)}
                                whileHover={{
                                    scale: 1.05,
                                    y: -10,
                                }}
                                transition={{ type: "spring", stiffness: 400 }}
                                sx={{
                                    width: { xs: '100%', sm: 280 },
                                    height: 280,
                                    background: `linear-gradient(135deg, ${alpha(solution.shadowColor, 0.1)} 0%, ${alpha(solution.shadowColor, 0.05)} 100%)`,
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha(solution.shadowColor, 0.1)}`,
                                    borderRadius: 4,
                                    p: 3,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    boxShadow: hoveredIndex === index
                                        ? `0 20px 40px ${alpha(solution.shadowColor, 0.3)}`
                                        : `0 10px 20px ${alpha(solution.shadowColor, 0.1)}`,
                                    transition: 'box-shadow 0.3s ease',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: solution.gradient,
                                        opacity: hoveredIndex === index ? 1 : 0.5,
                                        transition: 'opacity 0.3s ease'
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '24px',
                                        background: solution.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                        color: 'white',
                                        boxShadow: `0 10px 20px ${alpha(solution.shadowColor, 0.3)}`,
                                        transform: hoveredIndex === index ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                    }}
                                >
                                    {solution.icon}
                                </Box>

                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'white',
                                        mb: 2,
                                        background: hoveredIndex === index ? solution.gradient : 'white',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: hoveredIndex === index ? 'transparent' : 'white',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {solution.title}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: alpha('#fff', 0.7),
                                        lineHeight: 1.6,
                                        transition: 'color 0.3s ease',
                                        '&:hover': {
                                            color: alpha('#fff', 0.9),
                                        }
                                    }}
                                >
                                    {solution.description}
                                </Typography>

                                {/* Animated background gradient */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: hoveredIndex === index ? '50%' : '150%',
                                        left: '50%',
                                        width: '200%',
                                        height: '200%',
                                        background: `radial-gradient(circle at center, ${alpha(solution.accentColor, 0.15)} 0%, transparent 70%)`,
                                        transform: 'translate(-50%, -50%)',
                                        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                        pointerEvents: 'none',
                                        opacity: hoveredIndex === index ? 1 : 0,
                                    }}
                                />
                            </MotionCard>
                        </motion.div>
                    ))}
                </Stack>
            </Container>
        </Box>
    );
};
